/* eslint-disable @angular-eslint/prefer-inject, @angular-eslint/prefer-standalone -- Billing intentionally retains its tested NgModule constructor contract. */
import {Component, OnInit}                           from '@angular/core';
import {MatDialog}                                   from '@angular/material/dialog';
import {MatSnackBar}                                 from '@angular/material/snack-bar';
import {ActivatedRoute}                              from '@angular/router';
import {PurchaseCreditsComponent}                    from '@src/app/admin/settings/components/purchase-credits/purchase-credits.component';
import {SurveyorLicenceAssignmentDialogComponent}    from '@src/app/admin/settings/components/billing/surveyor-licence-assignment-dialog.component';
import {PaygSwitchDialogComponent, PaygSwitchTiming} from '@src/app/admin/settings/components/billing/payg-switch-dialog.component';
import {BillingPolicyService} from '@src/app/core/services/billing-policy.service';
import {BillingPolicyContract} from '@src/app/core/models/billing-policy-contract.model';
import {AuthService}                                 from '@src/app/core/services/auth.service';
import {DialogComponent}                             from '@src/app/shared/components/dialog/dialog.component';
import {
    BillingCatalogue, BillingOffer, BillingPlanSelection, CheckoutSession, PaymentService,
    BillingInvoicePreview, SurveyorBillingEstimate, SurveyorLicence, SurveyorLicenceUser
}                                                    from '@src/app/core/services/payment.service';
import {catchError, forkJoin, of}                    from 'rxjs';
import {billingTaxLabel} from '@src/app/shared/helpers/billing-display';

@Component({
    standalone: false,
    selector: 'app-billing',
    templateUrl: './billing.component.html',
    styleUrls: ['./billing.component.scss'],
})
export class BillingComponent implements OnInit
{
    public readonly taxLabel = billingTaxLabel;
    public loading = true;
    public working = false;
    public error: string | null = null;
    public checkoutUrl: string | null = null;
    public checkoutReturnMessage: string | null = null;
    public checkoutReturnError = false;
    private signupCheckout = false;
    private checkoutSessionId: string | null = null;
    public accessUnavailable = false;
    public licenceError: string | null = null;
    public policy: BillingPolicyContract | null = null;
    public catalogue: BillingCatalogue | null = null;
    public plans: any = null;
    public planState: any = null;
    public invoicePreviews: BillingInvoicePreview[] = [];
    public invoicePreviewLoading = false;
    public invoicePreviewError = false;
    public surveyors: SurveyorBillingEstimate | null = null;
    public licences: SurveyorLicence[] = [];
    public assignableUsers: SurveyorLicenceUser[] = [];
    public showAllLicences = false;
    public readonly initialLicenceLimit = 6;

    constructor(
        private readonly payments: PaymentService,
        private readonly policies: BillingPolicyService,
        private readonly auth: AuthService,
        private readonly route: ActivatedRoute,
        private readonly dialog: MatDialog,
        private readonly snackBar: MatSnackBar,
    )
    {
    }

    public ngOnInit(): void
    {
        this.signupCheckout = this.route.snapshot.queryParamMap.get('signup_checkout') === 'true';
        this.accessUnavailable = this.route.snapshot.queryParamMap.get('access_unavailable') === 'true';
        const sessionId = this.route.snapshot.queryParamMap.get('session_id');
        this.checkoutSessionId = sessionId;

        if (this.route.snapshot.queryParamMap.get('payment_cancelled') === 'true') {
            this.toast('The hosted checkout was cancelled. Your current access has not changed.');
        }

        if (sessionId) {
            this.checkCheckoutReturn(sessionId);
            return;
        }

        this.load();
    }

    public refresh(): void
    {
        const sessionId = this.checkoutSessionId ?? this.route.snapshot.queryParamMap.get('session_id');

        if (sessionId) {
            this.checkCheckoutReturn(sessionId);
        } else {
            this.load(true);
        }
    }

    private checkCheckoutReturn(sessionId: string): void
    {
        this.loading = true;
        this.payments.checkSession(sessionId).subscribe({
            next: (session: CheckoutSession) => {
                this.checkoutReturnError = session.state === 'failed' || session.state === 'cancelled';
                this.checkoutReturnMessage = {
                    open: 'Checkout is still open. Complete payment in Stripe, then refresh billing details.',
                    pending: 'Payment is being confirmed. Your access will update after Stripe confirmation; refresh billing details shortly.',
                    succeeded: 'Payment confirmed. Your billing details and access are being refreshed.',
                    failed: 'Payment failed. Your existing access has not changed; you can retry checkout.',
                    cancelled: 'Checkout was cancelled. Your existing access has not changed.',
                }[session.state] ?? 'Checkout status is unavailable. Refresh billing details to check again.';

                this.checkoutUrl = session.state === 'open' ? session.url ?? null : null;
                this.load(true);
            },
            error: () => {
                this.checkoutReturnMessage = 'Checkout could not be verified. No access has been granted from the return link. Refresh billing details to try again.';
                this.checkoutReturnError = true;
                this.load(true);
            },
        });
    }

    public load(forcePolicyRefresh = false): void
    {
        this.loading = true;
        this.error = null;
        this.licenceError = null;
        this.loadInvoicePreviews();

        forkJoin({
            catalogue: this.payments.getCatalogue(),
            plans: this.payments.getPlans(),
            planState: this.payments.getPlan(),
            surveyors: this.payments.getSurveyorBilling().pipe(catchError(() => of({
                status: 'setup_required' as const,
                estimated: true,
                calculated_at: new Date().toISOString(),
                active_surveyors: 0,
                proposed_surveyors: 0,
                included_surveyors: null,
                extra_surveyors: null,
                extra_capacity: null,
                available_extra_seats: null,
                reduction_at_renewal: null,
                monthly_unit_amount: null,
                accrued_amount: null,
                projected_amount: null,
                incremental_amount: null,
                ongoing_monthly_amount: null,
                next_billing_date: null,
                chargeable_activations_blocked: false,
                staff_follow_up: true,
            }))),
            licences: this.payments.getSurveyorLicences().pipe(catchError(() => {
                this.licenceError = 'Surveyor seats could not be loaded. Refresh to try again; no assignments or payments have changed.';
                return of({data: [], assignable_users: []});
            })),
        }).subscribe({
            next: async result => {
                this.catalogue = result.catalogue;
                this.plans = result.plans;
                this.planState = result.planState;
                this.surveyors = result.surveyors;
                this.licences = result.licences.data;
                this.assignableUsers = result.licences.assignable_users;
                const tenantId = this.auth.tenant?.id;
                this.policy = tenantId ? await this.policies.initialize(tenantId, forcePolicyRefresh) : null;
                this.loading = false;

                if (this.signupCheckout) {
                    this.signupCheckout = false;

                    if (this.planState.checkout_required === true) {
                        if (this.setupOffer) {
                            this.completeProSetup();
                        } else {
                            this.error = 'Your account is ready, but that Pro plan is unavailable. Choose a plan below to continue.';
                        }
                    }
                }
            },
            error: response => {
                this.error = response?.error?.message ?? response?.error?.error
                    ?? 'Billing information is temporarily unavailable. Please retry; no access or payment state has changed.';
                this.loading = false;
            },
        });
    }

    private loadInvoicePreviews(): void
    {
        this.invoicePreviews = [];
        this.invoicePreviewError = false;
        this.invoicePreviewLoading = true;
        this.payments.getBillingPreview().subscribe({
            next: response => {
                this.invoicePreviews = response.data;
                this.invoicePreviewLoading = false;
            },
            error: () => {
                this.invoicePreviewError = true;
                this.invoicePreviewLoading = false;
            },
        });
    }

    public choosePlan(plan: BillingPlanSelection): void
    {
        if (plan === 'payg' && this.isProPlan) {
            this.openPaygSwitchDialog();
            return;
        }

        this.working = true;
        this.error = null;

        this.payments.selectPlan(plan).subscribe({
            next: state => {
                this.planState = state;

                if (plan === 'payg' || state.checkout_required !== true) {
                    this.toast(plan === 'payg'
                        ? 'PAYG selected. Buy credits from an available pack when you need them.'
                        : 'Your current paid access is already active.');
                    this.working = false;
                    this.load(true);
                    return;
                }

                const interval = plan === 'pro_annual' ? 'year' : 'month';
                const offer = this.baseOffers.find(candidate => candidate.interval === interval);

                if (!offer) {
                    this.error = 'That Pro billing interval is not currently available. No plan change or payment has been made.';
                    this.working = false;
                    return;
                }

                this.startCheckout(offer);
            },
            error: response => this.fail(response),
        });
    }

    public get setupOffer(): BillingOffer | undefined
    {
        if (this.planState?.checkout_required !== true || this.planState?.billing_managed_externally || !['pro_monthly', 'pro_annual'].includes(this.planState.selected_plan)) {
            return undefined;
        }
        const interval = this.planState.selected_plan === 'pro_annual' ? 'year' : 'month';
        return this.baseOffers.find(offer => offer.interval === interval);
    }

    public completeProSetup(): void
    {
        const offer = this.setupOffer;
        if (this.working || !offer) {
            return;
        }
        if (this.planState.checkout?.state === 'open' && this.planState.checkout.url) {
            window.location.href = this.planState.checkout.url;
            return;
        }
        this.startCheckout(offer);
    }

    public startCheckout(offer: BillingOffer, checkoutTab?: Window | null): void
    {
        this.working = true;
        this.checkoutUrl = null;
        this.checkoutReturnMessage = null;
        this.error = null;
        const returnUrl = `${window.location.origin}${window.location.pathname}`;

        this.payments.checkout({
            price_id: offer.price_id,
            success_url: `${returnUrl}?session_id={CHECKOUT_SESSION_ID}`,
            cancel_url: `${returnUrl}?payment_cancelled=true`,
        }).subscribe({
            next: result => {
                if (!result?.url) {
                    checkoutTab?.close();
                    this.fail({error: {message: 'The hosted checkout could not be opened. Please retry.'}});
                    return;
                }

                if (checkoutTab) {
                    checkoutTab.location.href = result.url;
                    this.working = false;
                    return;
                }

                if (checkoutTab === null) {
                    this.checkoutUrl = result.url;
                    this.error = 'Your browser blocked the checkout tab. Use the secure checkout link below to continue.';
                    this.working = false;
                    return;
                }

                window.location.href = result.url;
            },
            error: response => {
                checkoutTab?.close();
                this.fail(response);
            },
        });
    }

    public manageBilling(): void
    {
        this.working = true;
        this.payments.getCustomerPortal().subscribe({
            next: result => {
                const url = result?.data?.billing_portal_url;
                if (url) {
                    window.location.href = url;
                } else {
                    this.fail({error: {message: 'The hosted billing portal could not be opened.'}});
                }
            },
            error: response => this.fail(response),
        });
    }

    public confirmSubscriptionCancellation(): void
    {
        const expires = this.subscriptionDate
            ? new Intl.DateTimeFormat('en-GB', {dateStyle: 'long'}).format(new Date(this.subscriptionDate))
            : 'the end of the current billing period';
        const confirmation = this.dialog.open(DialogComponent, {
            maxWidth: '480px',
            data: {
                title: 'Switch to PAYG at renewal?',
                message: `Your Pro access and surveyor seats will continue until <strong>${expires}</strong>. After that, the subscription will stop renewing and your account will move to PAYG. Your existing data will not be deleted.`,
                type: 'danger-outline',
                confirmButtonLabel: 'Switch at renewal',
                cancelButtonLabel: 'Keep Pro',
            },
        });

        confirmation.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.updateSubscriptionRenewal(false);
            }
        });
    }

    public openPaygSwitchDialog(): void
    {
        const renewalDate = this.planState?.subscription?.next_base_billing_at
            ?? this.planState?.subscription?.access_ends_at;

        if (!renewalDate) {
            this.error = 'The Pro renewal date could not be confirmed. Refresh billing details before switching plans.';
            return;
        }

        this.dialog.open<PaygSwitchDialogComponent, {
            renewalDate: string;
            alreadyScheduled: boolean
        }, PaygSwitchTiming>(PaygSwitchDialogComponent, {
            width: '92vw',
            maxWidth: '620px',
            data: {renewalDate, alreadyScheduled: this.planState?.subscription?.renewal_enabled === false},
        }).afterClosed().subscribe(timing => {
            if (timing === 'now') {
                this.switchToPaygNow();
            }
            if (timing === 'renewal') {
                this.updateSubscriptionRenewal(false);
            }
        });
    }

    private switchToPaygNow(): void
    {
        this.working = true;
        this.error = null;
        this.payments.cancelSubscriptionNow().subscribe({
            next: state => {
                this.planState = state;
                this.working = false;
                this.toast('Switched to PAYG. Your Pro subscription and surveyor seats have ended.');
                this.load(true);
            },
            error: response => this.fail(response),
        });
    }

    public resumeSubscription(): void
    {
        this.updateSubscriptionRenewal(true);
    }

    public openCreditPacks(): void
    {
        this.dialog.open(PurchaseCreditsComponent, {
            width: '92vw',
            maxWidth: '900px',
            panelClass: 'mobile-width',
            data: null,
        });
    }

    public buyLicence(): void
    {
        const offer = this.seatOffer;
        if (!offer) {
            this.error = 'Additional surveyor seats are not currently available.';
            return;
        }
        // Open the tab during the click, before the asynchronous checkout request.
        // Browsers can otherwise refuse to open the hosted page after the response arrives.
        const checkoutTab = window.open('', '_blank');
        if (checkoutTab) {
            checkoutTab.opener = null;
        }

        this.startCheckout(offer, checkoutTab);
    }

    public cancelLicence(licence: SurveyorLicence): void
    {
        const renewal = licence.renews_at
            ? new Intl.DateTimeFormat('en-GB', {dateStyle: 'long'}).format(new Date(licence.renews_at))
            : 'its next renewal';
        const confirmation = this.dialog.open(DialogComponent, {
            maxWidth: '480px',
            data: {
                title: 'Cancel surveyor seat?',
                message: `This unassigned surveyor seat will stop renewing on <strong>${renewal}</strong>. You can undo cancellation before then to make it available again.`,
                type: 'danger-outline',
                confirmButtonLabel: 'Cancel seat',
                cancelButtonLabel: 'Keep seat',
            },
        });

        confirmation.afterClosed().subscribe(confirmed => {
            if (!confirmed) {
                return;
            }

            this.working = true;
            this.payments.cancelSurveyorLicence(licence.id).subscribe({
                next: () => this.refreshLicenceState('Surveyor seat cancellation scheduled.'),
                error: response => this.fail(response),
            });
        });
    }

    public openLicenceAssignment(licence: SurveyorLicence): void
    {
        const assignment = this.dialog.open(SurveyorLicenceAssignmentDialogComponent, {
            width: '92vw',
            maxWidth: '520px',
            data: {licence, users: this.assignableUsers},
        });

        assignment.afterClosed().subscribe((userId: number | null) => {
            if (userId === null || userId === undefined) {
                return;
            }

            this.working = true;
            this.payments.assignSurveyorLicence(licence.id, userId).subscribe({
                next: () => this.refreshLicenceState(licence.user ? 'Surveyor seat reassigned.' : 'Surveyor seat assigned.'),
                error: response => this.fail(response),
            });
        });
    }

    public unassignLicence(licence: SurveyorLicence): void
    {
        if (!licence.user) {
            return;
        }

        const confirmation = this.dialog.open(DialogComponent, {
            maxWidth: '480px',
            data: {
                title: 'Unassign surveyor seat?',
                message: `<strong>${licence.user.name}</strong> will lose App access. Their web access is unchanged.`,
                type: 'danger-outline',
                confirmButtonLabel: 'Unassign',
                cancelButtonLabel: 'Keep assigned',
            },
        });

        confirmation.afterClosed().subscribe(confirmed => {
            if (!confirmed) {
                return;
            }

            this.working = true;
            this.payments.unassignSurveyorLicence(licence.id).subscribe({
                next: () => this.refreshLicenceState('Surveyor seat unassigned.'),
                error: response => this.fail(response),
            });
        });
    }

    public resumeLicence(licence: SurveyorLicence): void
    {
        this.working = true;
        this.payments.resumeSurveyorLicence(licence.id).subscribe({
            next: () => this.refreshLicenceState('The surveyor seat will continue.'),
            error: response => this.fail(response),
        });
    }

    public get baseOffers(): BillingOffer[]
    {
        return this.catalogue?.offers?.filter(offer => offer.kind === 'base' && offer.plan === 'pro') ?? [];
    }

    public get proSeatOffer(): BillingOffer | null
    {
        return this.catalogue?.offers?.find(offer => offer.kind === 'seat' && offer.plan === 'pro') ?? null;
    }

    public get annualBaseSaving(): number | null
    {
        const monthly = this.baseOffers.find(offer => offer.interval === 'month');
        const annual = this.baseOffers.find(offer => offer.interval === 'year' && offer.currency === monthly?.currency);
        return monthly && annual ? Math.max(0, monthly.amount * 12 - annual.amount) : null;
    }

    public get visibleLicences(): SurveyorLicence[]
    {
        return this.showAllLicences ? this.licences : this.licences.slice(0, this.initialLicenceLimit);
    }

    public get hiddenLicenceCount(): number
    {
        return Math.max(0, this.licences.length - this.initialLicenceLimit);
    }

    public toggleLicenceList(): void
    {
        this.showAllLicences = !this.showAllLicences;
    }

    public get seatOffer(): BillingOffer | null
    {
        return this.catalogue?.offers?.find(offer => offer.kind === 'seat' && offer.plan === this.currentPlan) ?? null;
    }

    public get currentPlan(): string | null
    {
        return this.policy?.plan ?? this.planState?.effective_policy?.plan ?? null;
    }

    public get currentPlanLabel(): string
    {
        switch (this.currentPlan) {
            case 'pro':
                return 'Pro';

            case 'grow':
                return 'Grow';

            case 'expert':
                return 'Expert';

            case 'grandfathered_payg':
                return 'Grandfathered PAYG';

            case 'payg':
                return 'Pay as you go';

            default:
                return 'Plan unavailable';
        }
    }

    public get isPaygPlan(): boolean
    {
        return ['payg', 'grandfathered_payg'].includes(this.currentPlan ?? '');
    }

    public get isProPlan(): boolean
    {
        return this.currentPlan === 'pro';
    }

    public get subscriptionDate(): string | null
    {
        if (!this.isProPlan || !this.planState?.subscription) {
            return null;
        }

        return this.planState.subscription.renewal_enabled === false
            ? this.planState.subscription.access_ends_at ?? null
            : this.planState.subscription.next_base_billing_at ?? null;
    }

    public get subscriptionDateLabel(): 'Next billing date' | 'Expires at' | null
    {
        if (!this.subscriptionDate) {
            return null;
        }

        return this.planState.subscription.renewal_enabled === false ? 'Expires at' : 'Next billing date';
    }

    public get creditBalance(): number | null
    {
        return this.auth.credits;
    }

    public get totalSurveyorSeats(): number | null
    {
        if (this.surveyors?.included_surveyors === null || this.surveyors?.included_surveyors === undefined) {
            return null;
        }

        return this.surveyors.included_surveyors + (this.surveyors.extra_capacity ?? 0);
    }

    public get assignableSurveyorSeats(): number | null
    {
        if (this.surveyors?.total_licences !== undefined) {
            return Math.max(this.surveyors.total_licences - (this.surveyors.cancellation_pending ?? 0), 0);
        }

        return this.totalSurveyorSeats;
    }

    public get availableSurveyorSeats(): number | null
    {
        if (this.assignableSurveyorSeats === null || !this.surveyors) {
            return null;
        }

        return Math.max(this.assignableSurveyorSeats - this.surveyors.active_surveyors, 0);
    }

    public get extraSurveyorSeats(): number
    {
        return this.surveyors?.extra_capacity ?? 0;
    }

    public get includedSurveyorsInUse(): number
    {
        return Math.min(this.surveyors?.active_surveyors ?? 0, this.surveyors?.included_surveyors ?? 0);
    }

    public get additionalSurveyorsInUse(): number
    {
        return this.surveyors?.extra_surveyors
            ?? Math.max(0, (this.surveyors?.active_surveyors ?? 0) - (this.surveyors?.included_surveyors ?? 0));
    }

    public get showBillingSummary(): boolean
    {
        return this.isProPlan
            && this.surveyors?.status === 'ready'
            && this.surveyors.base?.amount !== null
            && this.surveyors.base?.amount !== undefined;
    }

    public get baseBillingInterval(): 'month' | 'year' | null
    {
        return this.surveyors?.base?.interval ?? null;
    }

    public get upcomingPaymentTotal(): number | null
    {
        if (this.invoicePreviewLoading || this.invoicePreviewError || !this.invoicePreviews.length) {
            return null;
        }
        if (this.invoicePreviews.some(invoice => invoice.currency !== this.surveyors?.currency || !Number.isFinite(invoice.amount_due))) {
            return null;
        }

        return this.invoicePreviews.reduce((total, invoice) => total + invoice.amount_due, 0);
    }

    public get seatBillingDate(): string | null
    {
        return this.surveyors?.extras_ends_at ?? this.surveyors?.next_billing_date ?? null;
    }

    public get seatBillingDateLabel(): 'Expires at' | 'Next billing date'
    {
        return this.surveyors?.extras_ends_at ? 'Expires at' : 'Next billing date';
    }

    public get proBenefits(): string[]
    {
        const offer = this.baseOffers[0];

        if (!offer) {
            return [];
        }

        const benefits: string[] = [];

        if (this.plans?.plans?.pro?.unlimited_pins === true) {
            benefits.push('Unlimited surveys');
        }

        if (offer.included_surveyors) {
            benefits.push(`${offer.included_surveyors} surveyor seats included`);
        }

        const labels: Record<string, string> = {
            form_builder: 'Custom forms',
            maintenance_reminders: 'Maintenance reminders',
            site_documents: offer.document_storage_gb
                ? `${offer.document_storage_gb} GB document storage`
                : 'Site documents',
        };

        for (const key of offer.feature_keys ?? []) {
            benefits.push(labels[key] ?? key.replace(/_/g, ' '));
        }

        return benefits;
    }

    public money(amount: number | null | undefined, currency = 'gbp'): string
    {
        if (amount === null || amount === undefined) {
            return 'Unavailable';
        }

        return new Intl.NumberFormat('en-GB', {style: 'currency', currency: currency.toUpperCase()}).format(amount / 100);
    }

    private fail(response: any): void
    {
        this.error = response?.error?.message ?? response?.error?.error ?? 'The billing request could not be completed. Please retry.';
        this.working = false;
    }

    private refreshLicenceState(message: string): void
    {
        this.licenceError = null;
        this.loadInvoicePreviews();

        forkJoin({
            licences: this.payments.getSurveyorLicences(),
            surveyors: this.payments.getSurveyorBilling(),
        }).subscribe({
            next: ({licences, surveyors}) => {
                this.licences = licences.data;
                this.assignableUsers = licences.assignable_users;
                this.surveyors = surveyors;
                this.working = false;
                this.toast(message);
            },
            error: () => {
                this.licenceError = 'The surveyor seat was changed, but its details could not be refreshed. Use Refresh billing details to try again.';
                this.working = false;
                this.toast(message);
            },
        });
    }

    private toast(message: string): void
    {
        this.snackBar.open(message, 'Ok', {duration: 2500});
    }

    private updateSubscriptionRenewal(renew: boolean): void
    {
        this.working = true;
        this.error = null;

        const request = renew ? this.payments.resumeSubscription() : this.payments.cancelSubscription();

        request.subscribe({
            next: state => {
                this.planState = state;
                this.working = false;
                const expiry = state?.subscription?.access_ends_at
                    ? new Intl.DateTimeFormat('en-GB', {dateStyle: 'long'}).format(new Date(state.subscription.access_ends_at))
                    : null;
                this.snackBar.open(
                    renew
                        ? 'Your Pro subscription will continue.'
                        : expiry
                            ? `Cancellation scheduled. Your Pro access will expire on ${expiry}.`
                            : 'Cancellation scheduled. Your expiry date is being confirmed.',
                    'OK',
                    {duration: 6000},
                );
                this.load(true);
            },
            error: response => this.fail(response),
        });
    }
}
